import React, { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import p5 from "p5";



interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  history: { x: number; y: number }[];
}

interface PhilosophyItem {
  title: string;
  desc: string;
  icon: string;
}

export default function App() {
  const sketchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sketchRef.current) {
      const p = new p5((p: p5) => {
        const particles: Particle[] = [];
        const particleCount = 15;

        p.setup = () => {
          p.createCanvas(p.windowWidth, p.windowHeight);
          p.stroke(180, 150);
          p.strokeWeight(1.5);
          p.noFill();

          for (let i = 0; i < particleCount; i++) {
            particles.push({
              x: p.random(p.width),
              y: p.random(p.height),
              vx: p.random(-0.5, 0.5),
              vy: p.random(-0.5, 0.5),
              size: p.random(1, 3),
              history: [],
            });
          }
        };

        p.draw = () => {
          p.background(18, 18, 18, 5);

          particles.forEach((particle) => {
            particle.x += particle.vx;
            particle.y += particle.vy;

            if (particle.x < 0 || particle.x > p.width) particle.vx *= -1;
            if (particle.y < 0 || particle.y > p.height) particle.vy *= -1;

            particle.history.push({ x: particle.x, y: particle.y });
            if (particle.history.length > 20) particle.history.shift();

            const h = particle.history;
            if (h.length >= 4) {
              p.beginShape();
              p.vertex(h[0].x, h[0].y);
              for (let i = 0; i < h.length - 3; i++) {
                const cp1x = h[i+1].x + (h[i+2].x - h[i].x) / 6;
                const cp1y = h[i+1].y + (h[i+2].y - h[i].y) / 6;
                const cp2x = h[i+2].x - (h[i+3].x - h[i+1].x) / 6;
                const cp2y = h[i+2].y - (h[i+3].y - h[i+1].y) / 6;
                p.bezierVertex(cp1x, cp1y);
                p.bezierVertex(cp2x, cp2y);
                p.bezierVertex(h[i+2].x, h[i+2].y);
              }
              p.endShape();
            }
          });
        };

        p.windowResized = () => {
          p.resizeCanvas(p.windowWidth, p.windowHeight);
        };
      }, sketchRef.current);

      return () => p.remove();
    }
  }, []);

  const philosophyItems: PhilosophyItem[] = [
    {
      title: "悬置的意义",
      desc: "我们拒绝预设结果。我们的AI系统保持开放框架，人类意图在此完成最后的连接。",
      icon: "∞",
    },
    {
      title: "激进的未完成",
      desc: "算法永无'完成'之日——它们通过人类互动不断进化。每个缺陷都是与可能性的对话。",
      icon: "∅",
    },
    {
      title: "存在主义工具",
      desc: "不预设目的的技术，而是为数字时代的真实人类选择创造空间。",
      icon: "✸",
    },
  ];

  return (
    <div className="min-h-screen bg-[#0f0f0f] text-[#e6e6e6] font-sans overflow-x-hidden">
      <div ref={sketchRef} className="fixed inset-0 z-0 pointer-events-none" />

      <section className="relative min-h-screen flex items-center justify-center px-6 pt-24 pb-16">
        <div className="max-w-4xl mx-auto text-center z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <span className="inline-block px-4 py-1 mb-4 text-sm font-medium tracking-wider uppercase bg-[#8a252c]/10 text-[#d4a798] rounded-full border border-[#8a252c]/20">
              未完成之境
            </span>
            <h1 className="text-6xl md:text-8xl font-serif leading-none mb-6">
              <span className="block">悬解工场</span>
              <span className="block">Nefinita</span>
              <span className="block mt-2 text-[#a5a5a5] text-3xl md:text-4xl font-light">/ne.fiˈni.ta/</span>
            </h1>
            <p className="text-xl md:text-2xl text-[#c5c5c5] max-w-2xl mx-auto">
              人工智能在此拥抱未完成之美
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-16"
          >
            <div className="inline-block">
              <div className="border border-dashed border-[#8a252c] rounded-lg p-4 md:p-6 max-w-2xl mx-auto">
                <p className="text-lg md:text-xl italic text-[#d4a798]">
                  "我们不追求完美方案。<br className="hidden md:block" />
                  我们在人类选择与算法可能性之间<br className="hidden md:block" />
                  那片未完成的空间里锻造意义。"
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-20 flex flex-col sm:flex-row justify-center gap-4"
          >
            <button className="px-8 py-4 bg-transparent border-2 border-[#8a252c] text-[#e6e6e6] hover:bg-[#8a252c]/10 transition-colors rounded-sm uppercase tracking-wider font-medium">
              进入工场
            </button>
             <button
               onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })}
               className="px-8 py-4 bg-[#8a252c] text-white hover:bg-[#a33a3a] transition-colors rounded-sm uppercase tracking-wider font-medium"
             >
               未完成项目
            </button>
          </motion.div>
        </div>

        <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 z-10">
          <div className="w-16 h-16 border-2 border-dashed border-[#8a252c] rounded-full opacity-30 animate-pulse-slow" />
        </div>
      </section>

      <section className="relative py-24 px-6 bg-[#151515]">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-serif text-center mb-16"
          >
            Nefinita 未完成<span className="text-[#d4a798]">哲学</span>
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {philosophyItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="group relative p-8 border border-dashed border-[#3a3a3a] hover:border-[#8a252c] transition-all rounded-lg backdrop-blur-sm"
              >
                <div className="absolute -top-4 -left-4 w-16 h-16 flex items-center justify-center bg-[#1a1a1a] border border-dashed border-[#8a252c] rounded-full text-[#8a252c] text-xl font-bold z-10">
                  {item.icon}
                </div>
                <h3 className="text-2xl font-serif mt-2 mb-4 group-hover:text-[#d4a798] transition-colors">
                  {item.title}
                </h3>
                <p className="text-[#a5a5a5] leading-relaxed">
                  {item.desc}
                </p>
                <div className="mt-6 pt-4 border-t border-dashed border-[#3a3a3a]">
                  <span className="text-sm text-[#8a252c] uppercase tracking-wider">持续探索中</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="projects" className="relative py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="inline-block px-4 py-1 mb-4 text-sm font-medium tracking-wider uppercase bg-[#2a2a2a]/50 text-[#d4a798] rounded-full"
            >
              未完成作品
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-serif"
            >
              进行中的<span className="text-[#8a252c]">项目</span>
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {[1, 2].map((project, index) => (
              <motion.div
                key={project}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative group"
              >
                <div className="absolute -inset-1 bg-gradient-to-r from-[#8a252c]/30 to-transparent rounded-xl blur opacity-25 group-hover:opacity-40 transition duration-500 group-hover:duration-200" />
                  {index === 0 ? (
                    <a
                      href="https://github.com/nefinita/nefinita_kawase_deeplearn"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block relative bg-[#121212] border border-[#2a2a2a] rounded-xl overflow-hidden h-[400px] hover:border-[#8a252c] transition-colors"
                    >
                      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f]">
                        <div className="text-center p-8">
                          <div className="w-24 h-24 mx-auto mb-6 border-2 border-dashed border-[#8a252c] rounded-full flex items-center justify-center">
                            <span className="text-3xl text-[#8a252c]">✏️</span>
                          </div>
                          <h3 className="text-2xl font-serif mb-2">川瀨·悬解汇率</h3>
                          <p className="text-[#a5a5a5] max-w-md mx-auto">
                            汇率如庖丁眼中的牛——错综复杂，不可强行解析。此项目应顺市场之势，在多维注意力的交织中游刃有余，                             不追求确定性答案，唯在不确定性中导航。
                          </p>
                          <div className="mt-6 text-sm text-[#8a252c] font-mono">
                            基于 Nefinita AI
                          </div>
                          <div className="mt-4 flex justify-center">
                            <span className="inline-block px-4 py-1 bg-[#8a252c]/10 text-[#d4a798] text-sm rounded-full border border-[#8a252c]/20">
                              进行中
                            </span>
                          </div>
                        </div>
                      </div>
                    </a>
                  ) : (
                    <div className="relative bg-[#121212] border border-[#2a2a2a] rounded-xl overflow-hidden h-[400px]">
                      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f]">
                        <div className="text-center p-8">
                          <div className="w-24 h-24 mx-auto mb-6 border-2 border-dashed border-[#8a252c] rounded-full flex items-center justify-center">
                            <span className="text-3xl text-[#8a252c]">✏️</span>
                          </div>
                          <h3 className="text-2xl font-serif mb-2">项目 {String.fromCharCode(65 + index)}</h3>
                          <p className="text-[#a5a5a5] max-w-md mx-auto">
                            伦理框架作为活的文档——算法通过社区对话而非预设规则不断进化。
                          </p>
                          <div className="mt-8 flex justify-center">
                            <span className="inline-block px-4 py-1 bg-[#8a252c]/10 text-[#d4a798] text-sm rounded-full border border-[#8a252c]/20">
                              进行中
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-20 text-center"
          >
            <a
              href="https://github.com/nefinita"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-4 bg-transparent border-2 border-[#8a252c] text-[#e6e6e6] hover:bg-[#8a252c]/10 transition-colors rounded-sm uppercase tracking-wider font-medium"
            >
              查看所有未完成作品
            </a>
          </motion.div>
        </div>
      </section>

      <section className="relative py-24 px-6 bg-[#121212] border-t border-dashed border-[#3a3a3a]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <div className="inline-block mb-6">
              <div className="w-16 h-16 border-2 border-dashed border-[#8a252c] rounded-full flex items-center justify-center mx-auto text-[#8a252c] text-xl font-bold">
                ?
              </div>
            </div>
            <h2 className="text-4xl md:text-5xl font-serif mb-6">
              您的<span className="text-[#d4a798]">问题</span>
            </h2>
            <p className="text-xl text-[#a5a5a5] max-w-2xl mx-auto">
              我们没有最终答案。我们只有进行中的有意义对话。
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-12"
          >
            <div className="inline-block">
              <div className="border border-dashed border-[#8a252c] rounded-lg p-6 md:p-8 max-w-2xl mx-auto">
                <p className="text-lg md:text-xl text-[#d4a798] font-mono">
                  qinthirteen@outlook.com
                </p>
                <p className="mt-4 text-[#a5a5a5]">
                  我们将在3个工作日内回复您的咨询。<br />
                  我们的回复保持未完成状态——我们为您的回应留下空间。
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mt-20"
          >
            <div className="text-sm text-[#666] max-w-3xl mx-auto">
              <p className="mb-2">
                悬解工场 © 2025 · 所有意义皆为临时，有待人类修订
              </p>
              <p className="mb-2">
                <a
                  href="https://beian.miit.gov.cn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#8a252c] hover:text-[#a33a3a] transition-colors underline"
                >
                  浙ICP备2025215060号
                </a>
              </p>
              <p>
                这个数字空间拥抱存在的未完成本质。
                我们拒绝完美方案，选择真实的存在。
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
        className="fixed bottom-8 right-8 z-20 pointer-events-none"
      >
        <div className="w-32 h-32 border-2 border-dashed border-[#8a252c] rounded-full opacity-10 animate-pulse-slow" />
      </motion.div>
    </div>
  );
}
